import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  notificationsApi,
  Notification,
  NotificationTypeOption,
  NotificationRole,
  NotificationType,
} from '@/api/notifications';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Bell, ArrowLeft, Loader2 } from 'lucide-react';

export function NotificationFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [types, setTypes] = useState<NotificationTypeOption[]>([]);
  const [roles, setRoles] = useState<NotificationRole[]>([]);

  // Form state
  const [type, setType] = useState<NotificationType>('news');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [page, setPage] = useState('');
  const [url, setUrl] = useState('');
  const [startingDate, setStartingDate] = useState('');
  const [endingDate, setEndingDate] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load types and roles in parallel
      const [typesData, rolesData] = await Promise.all([
        notificationsApi.getTypes(),
        notificationsApi.getRoles(),
      ]);

      setTypes(typesData);
      setRoles(rolesData);

      // If editing, load the notification data
      if (id) {
        const notification = await notificationsApi.getById(parseInt(id));
        setType(notification.type);
        setName(notification.name);
        setMessage(notification.message);
        setPage(notification.page);
        setUrl(notification.url || '');
        setStartingDate(notification.startingDate || '');
        setEndingDate(notification.endingDate);
        setSelectedRoleIds(notification.roles.map((r) => r.id));
      }
    } catch (err) {
      setError('Erreur lors du chargement des donnees');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleSelectAllRoles = () => {
    if (selectedRoleIds.length === roles.length) {
      setSelectedRoleIds([]);
    } else {
      setSelectedRoleIds(roles.map((r) => r.id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    // Strip HTML tags to check if message has actual content
    const messageText = message.replace(/<[^>]*>/g, '').trim();
    if (!type || !name || !messageText || !page || !endingDate) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (startingDate && endingDate && new Date(startingDate) > new Date(endingDate)) {
      setError('La date de debut doit etre anterieure a la date de fin');
      return;
    }

    try {
      setSaving(true);

      const data = {
        type,
        name,
        message,
        page,
        url: url || undefined,
        startingDate: startingDate || undefined,
        endingDate,
        roleIds: selectedRoleIds,
      };

      if (isEditing) {
        await notificationsApi.update(parseInt(id!), data);
      } else {
        await notificationsApi.create(data);
      }

      navigate('/notifications');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/notifications">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Bell className="h-5 w-5" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            {isEditing ? 'Modifier la notification' : 'Nouvelle notification'}
          </h1>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Informations generales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select value={type} onValueChange={(value) => setType(value as NotificationType)}>
                  <SelectTrigger id="type">
                    <SelectValue placeholder="Selectionnez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="page">Page *</Label>
                <Input
                  id="page"
                  value={page}
                  onChange={(e) => setPage(e.target.value)}
                  placeholder="ex: dashboard, students, etc."
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Titre *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Titre de la notification"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Message *</Label>
              <RichTextEditor
                value={message}
                onChange={setMessage}
                placeholder="Contenu de la notification"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">Lien (base de connaissances)</Label>
              <Input
                id="url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Periode d'affichage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startingDate">Date de debut</Label>
                <Input
                  id="startingDate"
                  type="date"
                  value={startingDate}
                  onChange={(e) => setStartingDate(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Laissez vide pour afficher immediatement
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="endingDate">Date de fin *</Label>
                <Input
                  id="endingDate"
                  type="date"
                  value={endingDate}
                  onChange={(e) => setEndingDate(e.target.value)}
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Roles cibles</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectAllRoles}
            >
              {selectedRoleIds.length === roles.length ? 'Deselectionner tout' : 'Selectionner tout'}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {roles.map((role) => (
                <div key={role.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`role-${role.id}`}
                    checked={selectedRoleIds.includes(role.id)}
                    onCheckedChange={() => handleRoleToggle(role.id)}
                  />
                  <Label
                    htmlFor={`role-${role.id}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {role.name}
                  </Label>
                </div>
              ))}
            </div>
            {roles.length === 0 && (
              <p className="text-sm text-muted-foreground">Aucun role disponible</p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to="/notifications">Annuler</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Enregistrement...
              </>
            ) : isEditing ? (
              'Mettre a jour'
            ) : (
              'Creer'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
